import { useEffect, useState, ChangeEvent } from 'react';
import { api } from '../api/client';
import { UserCardResponse } from '../api/types';
import './MyCardPage.css';

type CardState =
  | { kind: 'loading' }
  | { kind: 'loaded'; card: UserCardResponse }
  | { kind: 'error'; message: string };

function MyCardPage() {
  const [cardState, setCardState] = useState<CardState>({ kind: 'loading' });
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [bankCard, setBankCard] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [activeMethod, setActiveMethod] = useState<'usdt' | 'card'>('usdt');

  useEffect(() => {
    let mounted = true;

    async function fetchCard() {
      try {
        const card = await api.getUserCard();
        if (mounted) {
          setCardState({ kind: 'loaded', card });
          setDisplayName(card.displayName ?? '');
          setDescription(card.description ?? '');
          setBankCard(card.bankCard ?? '');
          setPhotoUrl(card.photoUrl ?? '');
        }
      } catch (error) {
        if (mounted) {
          setCardState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Failed to load card',
          });
        }
      }
    }

    void fetchCard();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);
    setMessage(null);

    try {
      const updated = await api.updateUserCard({
        displayName: displayName.trim() || null,
        description: description.trim() || null,
        bankCard: bankCard.trim() || null,
      });
      setCardState({ kind: 'loaded', card: updated });
      setDisplayName(updated.displayName ?? '');
      setDescription(updated.description ?? '');
      setBankCard(updated.bankCard ?? '');
      setPhotoUrl(updated.photoUrl ?? '');
      setMessage('Saved!');
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('Invalid file type. Allowed: JPG, PNG, WebP');
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage('File size exceeds 5MB limit');
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setPhotoUploading(true);
    setMessage(null);

    try {
      const result = await api.uploadCardPhoto(file);
      setPhotoUrl(result.photoUrl);
      setMessage('Photo uploaded');
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to upload');
    } finally {
      setPhotoUploading(false);
      event.target.value = '';
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 1800);
    } catch {
      setCopyStatus('Failed');
      setTimeout(() => setCopyStatus(''), 1800);
    }
  };

  if (cardState.kind === 'loading') {
    return (
      <div className="my-card-page">
        <div className="my-card-loading">Loading your card...</div>
      </div>
    );
  }

  if (cardState.kind === 'error') {
    return (
      <div className="my-card-page">
        <div className="my-card-error">
          <h2>Card unavailable</h2>
          <p>{cardState.message}</p>
        </div>
      </div>
    );
  }

  const card = cardState.card;
  const finalDisplayName = displayName || (card.user.username ? `@${card.user.username}` : 'User');
  const currentPayment = card.paymentMethods?.[activeMethod];
  const currentUrl = currentPayment?.url || card.publicUrl;

  return (
    <div className="my-card-page">
      {/* Header */}
      <header className="my-card-header">
        <h1>My Payment Card</h1>
        <div className="my-card-tabs">
          <button
            type="button"
            className={`my-card-tab ${activeMethod === 'usdt' ? 'active' : ''}`}
            onClick={() => setActiveMethod('usdt')}
          >
            USDT
          </button>
          <button
            type="button"
            className={`my-card-tab ${activeMethod === 'card' ? 'active' : ''}`}
            onClick={() => setActiveMethod('card')}
            disabled={!card.paymentMethods?.card?.available}
          >
            Card
          </button>
        </div>
      </header>

      {/* Main content */}
      <div className="my-card-content">
        {/* Preview section */}
        <div className="my-card-preview">
          {/* Photo */}
          <label className="my-card-photo">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoChange}
              disabled={photoUploading}
            />
            {photoUrl ? (
              <img src={photoUrl} alt={finalDisplayName} />
            ) : (
              <div className="my-card-photo-placeholder">
                {photoUploading ? '...' : '+'}
              </div>
            )}
            {photoUploading && <div className="my-card-photo-uploading">Uploading...</div>}
          </label>

          {/* QR Code */}
          <div className="my-card-qr">
            {currentPayment?.available && currentPayment.qrCode ? (
              <img src={currentPayment.qrCode} alt="QR Code" />
            ) : (
              <div className="my-card-qr-placeholder">
                {activeMethod === 'usdt'
                  ? 'Wallet generating...'
                  : 'Add card number'}
              </div>
            )}
          </div>
        </div>

        {/* Editor section */}
        <div className="my-card-editor">
          <div className="my-card-field">
            <label>Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </div>

          <div className="my-card-field">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell clients about you"
              rows={2}
            />
          </div>

          <div className="my-card-field">
            <label>Card / Payment Link</label>
            <input
              type="text"
              value={bankCard}
              onChange={(e) => setBankCard(e.target.value)}
              placeholder="Card number or Monobank link"
            />
          </div>

          {message && (
            <p className={`my-card-message ${message.includes('Failed') || message.includes('Invalid') || message.includes('exceeds') ? 'error' : 'success'}`}>
              {message}
            </p>
          )}

          <button
            type="button"
            className="my-card-save-btn"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Link section */}
      <div className="my-card-link-section">
        <span className="my-card-link">{currentUrl}</span>
        <button type="button" onClick={() => copyLink(currentUrl || '')}>
          {copyStatus || 'Copy'}
        </button>
      </div>
    </div>
  );
}

export default MyCardPage;
